import type { JourneyResponse } from '@manasil/shared';
import { HOME_HERO } from '@manasil/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { resolveMediaUrl } from '../lib/api.js';
import {
  HERO_SPEC,
  IMAGE_ACCEPT,
  type ImageCheck,
  inspectImage,
  removeJourneyHero,
  uploadJourneyHero,
} from '../lib/image.js';

/**
 * The Home hero — the full-bleed image behind the greeting on artboard 2a.
 *
 * Deliberately a separate card from the cover rather than a second slot inside it: they are
 * different shapes with opposite safe areas, and an editor who thinks "it's the same picture"
 * is exactly the mistake this is here to prevent.
 *
 * The preview carries the app's real scrim, which on this screen runs LEFT to right, and the
 * real headline over it — because the one thing that reliably goes wrong is a subject on the
 * left, buried under "Better Thoughts. Brighter Days."
 */
export function HeroCard({
  journey,
  dayOneBanner = null,
}: {
  journey: JourneyResponse;
  /**
   * Day 1's story banner, which is what a shelf tile actually shows. Passed in because the
   * editor page already has the days loaded — no extra request for a preview.
   */
  dayOneBanner?: string | null;
}) {
  const client = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ file: File; check: ImageCheck } | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An object URL is a live handle to the file; letting them pile up leaks the file itself.
  useEffect(() => {
    return () => {
      if (pending) URL.revokeObjectURL(pending.check.previewUrl);
    };
  }, [pending]);

  async function choose(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    try {
      const check = await inspectImage(file, HERO_SPEC);
      if (pending) URL.revokeObjectURL(pending.check.previewUrl);
      setPending({ file, check });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not read that image');
    }
  }

  async function upload() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await uploadJourneyHero(journey.id, pending.file, pending.check, setProgress);
      await client.invalidateQueries({ queryKey: ['journey', journey.id] });
      URL.revokeObjectURL(pending.check.previewUrl);
      setPending(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Upload failed');
    } finally {
      setBusy(false);
      setProgress(0);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await removeJourneyHero(journey.id);
      await client.invalidateQueries({ queryKey: ['journey', journey.id] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not remove the hero');
    } finally {
      setBusy(false);
    }
  }

  /** A blob: URL while unsent, otherwise the stored hero — relative from the local driver. */
  const shown = pending
    ? pending.check.previewUrl
    : journey.heroUrl
      ? resolveMediaUrl(journey.heroUrl)
      : null;
  const blocked = (pending?.check.problems.length ?? 0) > 0;

  /**
   * What a shelf tile shows, mirroring `tileUrl` in homeShelfCardSchema: day 1's square
   * story banner first, then the hero, then the cover. Kept in step with the server by
   * hand — this is a preview, so drift shows as a misleading picture, not a broken app.
   */
  const tileRaw = dayOneBanner ?? journey.heroUrl ?? journey.coverUrl;
  const tileArt = tileRaw ? resolveMediaUrl(tileRaw) : null;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Home hero</h3>
          <p className="hint">
            {HOME_HERO.width} × {HOME_HERO.height} (1.38:1), JPEG, 150–300 KB. The headline sits
            over the LEFT {Math.round((HOME_HERO.obscuredFromLeft ?? 0) * 100)}% — keep the subject
            on the right. Shown behind the greeting on the app's home screen.
          </p>
        </div>
        {journey.heroUrl && !pending ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={clear} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>

      {error ? <div className="banner">{error}</div> : null}

      {/* A phone, not a strip.
          The hero fills only the top 282pt of an 844pt screen, so a full-width preview of
          the bare image tells an editor nothing about what the app shows. This frame is the
          top 430pt of the real screen at real proportions: the image, both of the app's
          scrims, the headline and streak pill that sit over it, and the first shelf
          beginning underneath — which is where the image stops. */}
      <div className="cover-previews">
        <div className="hero-phone" aria-label="How this image looks on the app's home screen">
          {shown ? (
            <>
              <div className="hero-shot">
                <img src={shown} alt="" className="cover-img" />
                <div className="hero-scrim" />
              </div>

              <div className="hero-chrome">
                <div className="hero-brand">
                  <div>
                    <div className="hero-wordmark">Manasil</div>
                    <div className="hero-tagline">Change Your Mind, Change Your Life.</div>
                  </div>
                  <span className="hero-streak">🔥 55</span>
                </div>

                <div className="hero-copy">
                  <div className="hero-eyebrow">A CALMER YOU</div>
                  <div className="hero-title">
                    Better
                    <br />
                    Thoughts.
                  </div>
                  <div className="hero-title hero-title-grad">Brighter Days.</div>
                  <div className="hero-sub">
                    Listen to stories. Build a
                    <br />
                    happier, stronger you.
                  </div>
                </div>

                <div className="hero-cta">▶ Continue Listening</div>
              </div>

              {/* Below the fold of the image: the app's own ground, and the first shelf. */}
              <div className="hero-below">
                <div className="hero-shelf-label">Your Favourites</div>
                <div className="hero-shelf-row">
                  {/* This journey's real tile in the first slot; the rest stand in for
                      whatever else is on the shelf. */}
                  <span className="hero-tile">
                    {tileArt ? <img src={tileArt} alt="" /> : null}
                    <em>{journey.title}</em>
                  </span>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </>
          ) : (
            <div className="cover-empty">No hero — home falls back to the cover</div>
          )}
          <span className="cover-tag">On home</span>
        </div>
      </div>

      {pending ? (
        <div className="stack" style={{ gap: 8, marginTop: 12 }}>
          <div className="hint nums">
            {pending.check.width} × {pending.check.height} · {pending.check.aspect.toFixed(2)}:1 ·{' '}
            {Math.round(pending.file.size / 1024)} KB
          </div>
          {pending.check.problems.map((problem) => (
            <div key={problem} className="banner">
              {problem}
            </div>
          ))}
          {pending.check.warnings.map((warning) => (
            <div key={warning} className="hint">
              {warning}
            </div>
          ))}
          {busy ? <progress value={progress} max={1} style={{ width: '100%' }} /> : null}
          <div className="row" style={{ gap: 9 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={upload}
              disabled={busy || blocked}
              title={blocked ? 'Fix the problems above first' : undefined}
            >
              {busy ? `Uploading ${Math.round(progress * 100)}%` : 'Use this hero'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                URL.revokeObjectURL(pending.check.previewUrl);
                setPending(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: 9, marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            {journey.heroUrl ? 'Replace hero' : 'Choose hero'}
          </button>
          {journey.heroWidth ? (
            <span className="hint nums">
              {journey.heroWidth} × {journey.heroHeight}
            </span>
          ) : null}
        </div>
      )}

      <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} hidden onChange={choose} />
    </div>
  );
}
